import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { customerUnitPrice } from '../utils/planningConstants';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ChevronRight, MapPin, ArrowRight, Loader2, AlertCircle, 
  Grid3X3, List, Filter
} from 'lucide-react';
import SEO from '../components/SEO';
import { cityName, categoryName, subCategoryName, cityState } from '../utils/seoFormat';
import { breadcrumbLD, itemListLD, collectionPageLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICING_MODEL_LABELS = {
  flat_rate: 'Flat Rate',
  per_hour: 'Per Hour',
  per_person: 'Per Person',
  per_hour_per_person: 'Per Hour/Person'
};

export default function CategoryListingPage() {
  const { city, category, subCategory } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    fetchListings();
  }, [city, category, subCategory]);

  const fetchListings = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API}/listings/${city}`;
      if (category) url += `/${category}`;
      if (subCategory) url += `/${subCategory}`;
      
      const response = await axios.get(url);
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch listings:', err);
      setError(err.response?.data?.detail || 'Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (product) => {
    const { pricing_model } = product;
    const v = customerUnitPrice(product).toFixed(2);
    switch (pricing_model) {
      case 'flat_rate': return `$${v}`;
      case 'per_hour': return `$${v}/hr`;
      case 'per_person': return `$${v}/person`;
      case 'per_hour_per_person': return `$${v}/hr/person`;
      default: return `$${v}`;
    }
  };

  const getProductUrl = (product) => {
    return `/suppliers/${city}/${product.category}/${product.sub_category}/${product.slug}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pt-20">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading services...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pt-20">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Not Found</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/plan-event')}>
              Browse All Services
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const cityDisplay = data?.city_name || cityName(city);
  const state = cityState(city);
  const locLabel = state ? `${cityDisplay}, ${state}` : cityDisplay;
  const categoryLabel = categoryName(category);
  const subCategoryLabel = subCategoryName(subCategory);

  // SEO copy ---------------------------------------------------------------
  const pageHeader = subCategory ? subCategoryLabel : (category ? categoryLabel : `Services in ${cityDisplay}`);
  const seoTitle = subCategory
    ? `${subCategoryLabel} in ${cityDisplay}, ${state} — Book Top-Rated ${categoryLabel}`
    : category
      ? `${categoryLabel} in ${cityDisplay}${state ? ', ' + state : ''} — Compare & Book Instantly`
      : `${cityDisplay} Event Services — Caterers, DJs, Venues, Staff & More`;
  const seoDescription = subCategory
    ? `Browse ${data?.total || 'top'} ${subCategoryLabel.toLowerCase()} options in ${locLabel}. Transparent pricing, instant booking, escrow protection. Eventism connects you with vetted ${categoryLabel.toLowerCase()} pros.`
    : category
      ? `The ${cityDisplay} marketplace for ${categoryLabel.toLowerCase()}. Real reviews, real pricing, instant availability. Book in minutes for parties, weddings, and corporate events.`
      : `Plan unforgettable events in ${locLabel}. Catering, bars, entertainment, staff, decor & venues — all in one place. Compare prices, see availability, and book with confidence.`;
  const seoKeywords = [
    `${cityDisplay} ${categoryLabel.toLowerCase()}`,
    `${categoryLabel.toLowerCase()} ${cityDisplay}`,
    subCategory ? `${subCategoryLabel} ${cityDisplay}` : `${cityDisplay} event services`,
    subCategory ? `book ${subCategoryLabel.toLowerCase()} ${cityDisplay}` : `book event services ${cityDisplay}`,
    `${cityDisplay} event planning`,
    `${cityDisplay} party services`,
    `${cityDisplay} wedding ${categoryLabel.toLowerCase()}`,
    `${cityDisplay} corporate event ${categoryLabel.toLowerCase()}`,
    state ? `${state} ${categoryLabel.toLowerCase()}` : '',
    'Eventism',
  ].filter(Boolean);

  const breadcrumbItems = [{ name: 'Home', url: '/' }, { name: cityDisplay, url: `/suppliers/${city}` }];
  if (category) breadcrumbItems.push({ name: categoryLabel, url: `/suppliers/${city}/${category}` });
  if (subCategory) breadcrumbItems.push({ name: subCategoryLabel, url: `/suppliers/${city}/${category}/${subCategory}` });

  const listLD = itemListLD(
    (data?.products || []).map((p) => ({
      name: p.name,
      url: `/suppliers/${city}/${p.category}/${p.sub_category}/${p.slug}`,
      image_url: p.image_url,
    })),
    pageHeader,
  );
  const seoLD = [collectionPageLD(seoTitle, seoDescription), breadcrumbLD(breadcrumbItems), listLD];

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        type="website"
        noindex={(city || '').toLowerCase() !== 'denver'}
        structuredData={seoLD}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6 flex-wrap" data-testid="breadcrumbs">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to={`/suppliers/${city}`} className="hover:text-foreground">{cityDisplay}</Link>
          {category && (
            <>
              <ChevronRight className="h-4 w-4" />
              <Link to={`/suppliers/${city}/${category}`} className="hover:text-foreground">{categoryLabel}</Link>
            </>
          )}
          {subCategory && (
            <>
              <ChevronRight className="h-4 w-4" />
              <span className="text-foreground">{subCategoryLabel}</span>
            </>
          )}
        </nav>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="h-5 w-5 text-indigo-500" />
            <span className="text-muted-foreground">{locLabel}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="listing-title">
            {pageHeader}
          </h1>
          <p className="text-muted-foreground">
            {data?.total || 0} services available
          </p>
        </div>

        {/* Sub-category filter (only show on category page) */}
        {category && !subCategory && data?.sub_categories?.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filter by type</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                className="text-xs"
                onClick={() => navigate(`/suppliers/${city}/${category}`)}
              >
                All {categoryLabel}
              </Button>
              {data.sub_categories.map(sub => (
                <Button
                  key={sub}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => navigate(`/suppliers/${city}/${category}/${sub}`)}
                >
                  {subCategoryName(sub)}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* View toggle */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted-foreground">
            Showing {data?.products?.length || 0} results
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Products */}
        {data?.products?.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-muted-foreground mb-4">No services found in this category</p>
              <Button onClick={() => navigate('/plan-event')}>
                Browse All Services
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data?.products?.map(product => (
              <Card 
                key={product.service_id} 
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => navigate(getProductUrl(product))}
                data-testid={`product-card-${product.slug}`}
              >
                {product.image_url && (
                  <div className="h-48 overflow-hidden relative">
                    <img 
                      src={product.image_url} 
                      alt={product.name} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                    />
                    {product.is_featured && (
                      <Badge className="absolute top-3 right-3 bg-amber-500">Featured</Badge>
                    )}
                  </div>
                )}
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg group-hover:text-indigo-500 transition-colors">
                    {product.name}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Badge variant="secondary">{categoryName(product.category)}</Badge>
                    {product.sub_category && (
                      <Badge variant="outline" className="text-xs">
                        {subCategoryName(product.sub_category)}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pb-2">
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {product.short_description || product.description}
                  </p>
                </CardContent>
                <CardFooter className="pt-2 flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-indigo-500">{formatPrice(product)}</span>
                    <p className="text-xs text-muted-foreground">
                      {PRICING_MODEL_LABELS[product.pricing_model]}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-indigo-500 transition-colors" />
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {data?.products?.map(product => (
              <Card 
                key={product.service_id} 
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(getProductUrl(product))}
                data-testid={`product-list-${product.slug}`}
              >
                <div className="flex">
                  {product.image_url && (
                    <div className="w-48 h-32 flex-shrink-0">
                      <img 
                        src={product.image_url} 
                        alt={product.name} 
                        className="w-full h-full object-cover" 
                      />
                    </div>
                  )}
                  <div className="flex-1 p-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{product.name}</h3>
                      <div className="flex gap-2 mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {categoryName(product.category)}
                        </Badge>
                        {product.sub_category && (
                          <Badge variant="outline" className="text-xs">
                            {subCategoryName(product.sub_category)}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {product.short_description}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-indigo-500">{formatPrice(product)}</span>
                      <p className="text-xs text-muted-foreground">
                        {PRICING_MODEL_LABELS[product.pricing_model]}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 text-center">
          <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-foreground border-0">
            <CardContent className="py-8">
              <h2 className="text-2xl font-bold mb-2">Ready to plan your event?</h2>
              <p className="text-foreground/80 mb-6">Get an instant quote with our event planning tool</p>
              <Button 
                size="lg" 
                className="bg-white text-indigo-600 hover:bg-white/90"
                onClick={() => navigate('/plan-event')}
              >
                Get Instant Quote <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
